/**
 * Requêtes et mutations TanStack Query.
 *
 * Les clés sont centralisées : c'est ce qui permet à une mutation d'invalider exactement ce
 * qu'elle a changé. Un changement de statut de stock invalide le stock ET le tableau de bord
 * (les compteurs en dépendent) ET le diagnostic de l'appareil concerné — pas tout le cache, ce
 * qui rechargerait la carte du parc pour rien.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  DesassociationForceeRequete,
  DesassociationForceeReponse,
  DiagnosticAppareilReponse,
  ImportStockRequete,
  ImportStockReponse,
  ListeJournalAuditRequete,
  ListeJournalAuditReponse,
  RechercheStockRequete,
  RechercheStockReponse,
} from '@genviz/shared/dto';

import { RAFRAICHISSEMENT_CARTE_MS, RAFRAICHISSEMENT_TABLEAU_MS, TAILLE_PAGE } from '../config';
import { api, type ErreurApi } from './client';
import type {
  ChangementStatutExploitationRequete,
  ChangementStatutExploitationReponse,
  ChangementStatutStockRequete,
  ChangementStatutStockReponse,
  ChangementStatutUtilisateurRequete,
  ChangementStatutUtilisateurReponse,
  DetailExploitationReponse,
  FiltreHistoriqueTramesRequete,
  LigneImpulsionHistorique,
  LigneMesureHistorique,
  LigneTrameHistorique,
  ListeExploitationsRequete,
  ListeExploitationsReponse,
  MesuresRecentesRequete,
  MesuresRecentesReponse,
  PageHistorique,
  ChangementStatutAbonnementRequete,
  ChangementStatutTicketRequete,
  ReponseTicketRequete,
  SouscriptionRequete,
  SupervisionCarteRequete,
  SupervisionCarteReponse,
  TableauDeBordReponse,
  EtalonnageReponse,
  EtalonnageRequete,
  EtalonnageResultat,
} from './types';

/* -------------------------------------------------------------------------- */
/* Clés                                                                        */
/* -------------------------------------------------------------------------- */

export const cles = {
  tableauDeBord: ['tableau-de-bord'] as const,
  stock: (filtres: RechercheStockRequete) => ['stock', filtres] as const,
  diagnostic: (sn: string, depuis: string | undefined, limiteTrames: number) =>
    ['diagnostic', sn, depuis ?? null, limiteTrames] as const,
  mesures: (sn: string, filtres: MesuresRecentesRequete) => ['mesures', sn, filtres] as const,
  historique: (sn: string, jeu: JeuHistorique, filtres: FiltreHistoriqueTramesRequete) =>
    ['historique', sn, jeu, filtres] as const,
  exploitations: (filtres: ListeExploitationsRequete) => ['exploitations', filtres] as const,
  exploitation: (id: string) => ['exploitation', id] as const,
  carte: (filtres: SupervisionCarteRequete) => ['carte-supervision', filtres] as const,
  audit: (filtres: ListeJournalAuditRequete) => ['audit', filtres] as const,
} as const;

/* -------------------------------------------------------------------------- */
/* Lectures                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Tableau de bord — rafraîchi périodiquement, y compris en arrière-plan.
 * C'est le seul écran où l'on paie ce coût : il sert de veille, il doit vieillir tout seul.
 */
export function useTableauDeBord(): UseQueryResult<TableauDeBordReponse, ErreurApi> {
  return useQuery<TableauDeBordReponse, ErreurApi>({
    queryKey: cles.tableauDeBord,
    queryFn: ({ signal }) => api.tableauDeBord.charger(signal),
    refetchInterval: RAFRAICHISSEMENT_TABLEAU_MS,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}

/** Recherche de stock, paginée par curseur opaque. */
export function useStock(
  filtres: RechercheStockRequete,
): UseInfiniteQueryResult<InfiniteData<RechercheStockReponse, string | undefined>, ErreurApi> {
  return useInfiniteQuery<
    RechercheStockReponse,
    ErreurApi,
    InfiniteData<RechercheStockReponse, string | undefined>,
    ReturnType<typeof cles.stock>,
    string | undefined
  >({
    queryKey: cles.stock(filtres),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) =>
      api.stock.rechercher(
        { ...filtres, limite: filtres.limite ?? TAILLE_PAGE, ...(pageParam ? { curseur: pageParam } : {}) },
        signal,
      ),
    getNextPageParam: (derniere) => derniere.curseur_suivant ?? undefined,
    staleTime: 15_000,
  });
}

export function useDiagnostic(
  numeroSerie: string,
  options: { depuis?: string; limiteTrames?: number } = {},
): UseQueryResult<DiagnosticAppareilReponse, ErreurApi> {
  const limiteTrames = options.limiteTrames ?? 50;
  return useQuery<DiagnosticAppareilReponse, ErreurApi>({
    queryKey: cles.diagnostic(numeroSerie, options.depuis, limiteTrames),
    queryFn: ({ signal }) =>
      api.appareils.diagnostic(
        numeroSerie,
        { ...(options.depuis ? { depuis: options.depuis } : {}), limite_trames: limiteTrames },
        signal,
      ),
    enabled: numeroSerie !== '',
    staleTime: 10_000,
  });
}

export function useMesures(
  numeroSerie: string,
  filtres: MesuresRecentesRequete,
): UseQueryResult<MesuresRecentesReponse, ErreurApi> {
  return useQuery<MesuresRecentesReponse, ErreurApi>({
    queryKey: cles.mesures(numeroSerie, filtres),
    queryFn: ({ signal }) => api.appareils.mesures(numeroSerie, filtres, signal),
    enabled: numeroSerie !== '',
    staleTime: 30_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Historique complet d'un appareil                                            */
/* -------------------------------------------------------------------------- */

export type JeuHistorique = 'mesures' | 'trames' | 'impulsions';

export interface LignesHistorique {
  mesures: LigneMesureHistorique;
  trames: LigneTrameHistorique;
  impulsions: LigneImpulsionHistorique;
}

/** Une page d'un des trois historiques — c'est aussi ce qu'utilise l'export, page après page. */
export function chargerPageHistorique<J extends JeuHistorique>(
  sn: string,
  jeu: J,
  filtres: FiltreHistoriqueTramesRequete,
  signal?: AbortSignal,
): Promise<PageHistorique<LignesHistorique[J]>> {
  const { type, recherche, ...communs } = filtres;
  if (jeu === 'trames') {
    return api.appareils.historiqueTrames(
      sn,
      { ...communs, ...(type ? { type } : {}), ...(recherche ? { recherche } : {}) },
      signal,
    ) as Promise<PageHistorique<LignesHistorique[J]>>;
  }
  if (jeu === 'impulsions') {
    return api.appareils.historiqueImpulsions(sn, communs, signal) as Promise<
      PageHistorique<LignesHistorique[J]>
    >;
  }
  return api.appareils.historiqueMesures(sn, communs, signal) as Promise<
    PageHistorique<LignesHistorique[J]>
  >;
}

/**
 * Historique paginé par curseur. `direct` relance la requête toutes les 10 s : pendant une
 * séance de banc, la page suit les trames qui arrivent sans qu'on clique sur « Actualiser ».
 */
export function useHistorique<J extends JeuHistorique>(
  sn: string,
  jeu: J,
  filtres: FiltreHistoriqueTramesRequete,
  options: { direct?: boolean } = {},
): UseInfiniteQueryResult<
  InfiniteData<PageHistorique<LignesHistorique[J]>, string | undefined>,
  ErreurApi
> {
  return useInfiniteQuery<
    PageHistorique<LignesHistorique[J]>,
    ErreurApi,
    InfiniteData<PageHistorique<LignesHistorique[J]>, string | undefined>,
    ReturnType<typeof cles.historique>,
    string | undefined
  >({
    queryKey: cles.historique(sn, jeu, filtres),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) =>
      chargerPageHistorique(sn, jeu, { ...filtres, ...(pageParam ? { curseur: pageParam } : {}) }, signal),
    getNextPageParam: (derniere) => derniere.curseur_suivant ?? undefined,
    enabled: sn !== '',
    staleTime: 5_000,
    refetchInterval: options.direct ? 10_000 : false,
  });
}

/**
 * Liste des exploitations.
 *
 * `actif` permet de ne PAS émettre la requête quand l'opérateur n'a pas la capacité de consulter
 * les exploitations : un opérateur usine ouvrant la carte de supervision n'a pas à provoquer un
 * 403 pour peupler une liste déroulante qu'il n'a pas le droit de voir.
 */
export function useExploitations(
  filtres: ListeExploitationsRequete,
  options: { actif?: boolean } = {},
): UseQueryResult<ListeExploitationsReponse, ErreurApi> {
  return useQuery<ListeExploitationsReponse, ErreurApi>({
    queryKey: cles.exploitations(filtres),
    queryFn: ({ signal }) => api.exploitations.lister(filtres, signal),
    enabled: options.actif ?? true,
    staleTime: 30_000,
  });
}

export function useExploitation(
  id: string,
): UseQueryResult<DetailExploitationReponse, ErreurApi> {
  return useQuery<DetailExploitationReponse, ErreurApi>({
    queryKey: cles.exploitation(id),
    queryFn: ({ signal }) => api.exploitations.detail(id, signal),
    enabled: id !== '',
  });
}

export function useCarteSupervision(
  filtres: SupervisionCarteRequete,
): UseQueryResult<SupervisionCarteReponse, ErreurApi> {
  return useQuery<SupervisionCarteReponse, ErreurApi>({
    queryKey: cles.carte(filtres),
    queryFn: ({ signal }) => api.supervision.carte(filtres, signal),
    refetchInterval: RAFRAICHISSEMENT_CARTE_MS,
    staleTime: 20_000,
  });
}

export function useAudit(
  filtres: ListeJournalAuditRequete,
): UseInfiniteQueryResult<InfiniteData<ListeJournalAuditReponse, string | undefined>, ErreurApi> {
  return useInfiniteQuery<
    ListeJournalAuditReponse,
    ErreurApi,
    InfiniteData<ListeJournalAuditReponse, string | undefined>,
    ReturnType<typeof cles.audit>,
    string | undefined
  >({
    queryKey: cles.audit(filtres),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) =>
      api.audit.lister(
        { ...filtres, limite: filtres.limite ?? TAILLE_PAGE, ...(pageParam ? { curseur: pageParam } : {}) },
        signal,
      ),
    getNextPageParam: (derniere) => derniere.curseur_suivant ?? undefined,
    staleTime: 20_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Écritures                                                                   */
/* -------------------------------------------------------------------------- */

export function useImportStock(): UseMutationResult<ImportStockReponse, ErreurApi, ImportStockRequete> {
  const cache = useQueryClient();
  return useMutation<ImportStockReponse, ErreurApi, ImportStockRequete>({
    mutationFn: (corps) => api.stock.importer(corps),
    onSuccess: (reponse) => {
      // Une simulation n'écrit rien : invalider le cache donnerait l'illusion du contraire.
      if (reponse.simulation) return;
      void cache.invalidateQueries({ queryKey: ['stock'] });
      void cache.invalidateQueries({ queryKey: cles.tableauDeBord });
    },
  });
}

export function useChangerStatutStock(
  numeroSerie: string,
): UseMutationResult<ChangementStatutStockReponse, ErreurApi, ChangementStatutStockRequete> {
  const cache = useQueryClient();
  return useMutation<ChangementStatutStockReponse, ErreurApi, ChangementStatutStockRequete>({
    mutationFn: (corps) => api.stock.changerStatut(numeroSerie, corps),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['stock'] });
      void cache.invalidateQueries({ queryKey: ['diagnostic', numeroSerie] });
      void cache.invalidateQueries({ queryKey: cles.tableauDeBord });
    },
  });
}

export function useEtalonnage(numeroSerie: string): UseQueryResult<EtalonnageReponse, ErreurApi> {
  return useQuery<EtalonnageReponse, ErreurApi>({
    queryKey: ['etalonnage', numeroSerie],
    queryFn: ({ signal }) => api.appareils.etalonnage(numeroSerie, signal),
    enabled: numeroSerie !== '',
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}

export function useEtalonner(
  numeroSerie: string,
): UseMutationResult<EtalonnageResultat, ErreurApi, EtalonnageRequete> {
  const cache = useQueryClient();
  return useMutation<EtalonnageResultat, ErreurApi, EtalonnageRequete>({
    mutationFn: (corps) => api.appareils.etalonner(numeroSerie, corps),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['etalonnage', numeroSerie] });
      void cache.invalidateQueries({ queryKey: ['diagnostic', numeroSerie] });
    },
  });
}

export function useDesassocier(
  numeroSerie: string,
): UseMutationResult<DesassociationForceeReponse, ErreurApi, DesassociationForceeRequete> {
  const cache = useQueryClient();
  return useMutation<DesassociationForceeReponse, ErreurApi, DesassociationForceeRequete>({
    mutationFn: (corps) => api.appareils.desassocier(numeroSerie, corps),
    onSuccess: () => {
      // Une désassociation touche l'appareil, le stock, l'exploitation d'origine et les compteurs.
      void cache.invalidateQueries({ queryKey: ['diagnostic', numeroSerie] });
      void cache.invalidateQueries({ queryKey: ['stock'] });
      void cache.invalidateQueries({ queryKey: ['exploitation'] });
      void cache.invalidateQueries({ queryKey: ['carte-supervision'] });
      void cache.invalidateQueries({ queryKey: cles.tableauDeBord });
    },
  });
}

export function useChangerStatutExploitation(
  id: string,
): UseMutationResult<
  ChangementStatutExploitationReponse,
  ErreurApi,
  ChangementStatutExploitationRequete
> {
  const cache = useQueryClient();
  return useMutation<
    ChangementStatutExploitationReponse,
    ErreurApi,
    ChangementStatutExploitationRequete
  >({
    mutationFn: (corps) => api.exploitations.changerStatut(id, corps),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: cles.exploitation(id) });
      void cache.invalidateQueries({ queryKey: ['exploitations'] });
      void cache.invalidateQueries({ queryKey: cles.tableauDeBord });
    },
  });
}

export function useChangerStatutUtilisateur(
  exploitationId: string,
): UseMutationResult<
  ChangementStatutUtilisateurReponse,
  ErreurApi,
  { id: string } & ChangementStatutUtilisateurRequete
> {
  const cache = useQueryClient();
  return useMutation<
    ChangementStatutUtilisateurReponse,
    ErreurApi,
    { id: string } & ChangementStatutUtilisateurRequete
  >({
    mutationFn: ({ id, ...corps }) => api.exploitations.changerStatutUtilisateur(id, corps),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: cles.exploitation(exploitationId) });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Exploitation commerciale                                                    */
/* -------------------------------------------------------------------------- */

export const CLES_COMMERCIAL = {
  clients: (filtre: unknown) => ['clients', filtre] as const,
  consommation: (filtre: unknown) => ['consommation', filtre] as const,
  abonnements: (filtre: unknown) => ['abonnements', filtre] as const,
  tickets: (filtre: unknown) => ['tickets', filtre] as const,
  ticket: (id: string) => ['ticket', id] as const,
};

export function useClients(filtre: Record<string, string | number | undefined> = {}) {
  return useQuery({
    queryKey: CLES_COMMERCIAL.clients(filtre),
    queryFn: ({ signal }) => api.commercial.clients(filtre, signal),
  });
}

export function useConsommation(filtre: Record<string, string | number | undefined> = {}) {
  return useQuery({
    queryKey: CLES_COMMERCIAL.consommation(filtre),
    queryFn: ({ signal }) => api.commercial.consommation(filtre, signal),
  });
}

export function useAbonnements(filtre: Record<string, string | number | undefined> = {}) {
  return useQuery({
    queryKey: CLES_COMMERCIAL.abonnements(filtre),
    queryFn: ({ signal }) => api.commercial.abonnements(filtre, signal),
  });
}

/**
 * Souscription. Invalide les abonnements ET les clients : la ligne d'un client porte son
 * abonnement, elle deviendrait fausse sans cette seconde invalidation.
 */
export function useSouscrire() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (corps: SouscriptionRequete) => api.commercial.souscrire(corps),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['abonnements'] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useChangerStatutAbonnement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...corps }: ChangementStatutAbonnementRequete & { id: string }) =>
      api.commercial.changerStatutAbonnement(id, corps),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['abonnements'] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useTickets(filtre: Record<string, string | number | undefined> = {}) {
  return useQuery({
    queryKey: CLES_COMMERCIAL.tickets(filtre),
    queryFn: ({ signal }) => api.commercial.tickets(filtre, signal),
    // La file du support bouge sans qu'on la sollicite : un ticket peut arriver pendant qu'on
    // la regarde. Rafraîchissement plus court que le reste du back-office.
    refetchInterval: 60_000,
  });
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: CLES_COMMERCIAL.ticket(id ?? ''),
    queryFn: ({ signal }) => api.commercial.ticket(id as string, signal),
    enabled: typeof id === 'string' && id.length > 0,
  });
}

export function useRepondreTicket(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (corps: ReponseTicketRequete) => api.commercial.repondreTicket(id, corps),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ticket', id] });
      void qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}

export function useChangerStatutTicket(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (corps: ChangementStatutTicketRequete) =>
      api.commercial.changerStatutTicket(id, corps),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ticket', id] });
      void qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}
